import React, { useState } from "react";
import { Table, Collapse, Badge } from "reactstrap";
import style from "./Items.module.scss";
import ChangesActions from "./ChangesActions";
import CircularProgress from "../../../CircularProgress/CircularProgress";

// Presentational copy of ChangesModal — same data semantics, no Modal wrapper,
// laid out to fill the full width of the host container with semantic colors
// (current = red, updated = green).

const COLOR_PREV = "#c0392b";
const COLOR_NEW = "#27ae60";
const COLOR_UNCHANGED = "#90a4ae";

const wrapperStyle = {
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "16px",
};

const sectionCardStyle = {
  width: "100%",
  background: "#ffffff",
  border: "1px solid #e3e8ec",
  borderRadius: "6px",
  padding: "16px 18px",
};

const sectionTitleStyle = {
  fontSize: "12px",
  fontWeight: 600,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  color: "#455a64",
  marginBottom: "12px",
};

const headerCardStyle = {
  width: "100%",
  background: "#f8f9fb",
  border: "1px solid #e3e8ec",
  borderRadius: "6px",
  padding: "14px 18px",
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: "30px",
};

const tableStyle = {
  width: "100%",
  marginBottom: 0,
};

const prevCellStyle = { color: COLOR_PREV, fontWeight: 500 };
const newCellStyle = { color: COLOR_NEW, fontWeight: 600 };
const unchangedCellStyle = { color: COLOR_UNCHANGED };

// Locale that uses "." for thousands and "," for decimals (Paraguay / Latin
// America). Hardcoded to ensure the same display regardless of the user's
// browser locale.
const NUMBER_LOCALE = "es-PY";

// Formats a transactional limit value with locale grouping. Accepts numbers
// AND numeric strings (the backend can send either). Non-numeric strings are
// returned as-is so things like "PYG 25.000" don't get broken.
const formatLimit = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  let num;
  if (typeof value === "number") {
    num = value;
  } else {
    const str = String(value).trim();
    if (!/^-?\d+(\.\d+)?$/.test(str)) return str;
    num = Number(str);
    if (!Number.isFinite(num)) return str;
  }
  const hasDecimals = String(value).includes(".");
  return new Intl.NumberFormat(NUMBER_LOCALE, {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: hasDecimals ? 2 : 0,
  }).format(num);
};

// Attempts to format a value as a number with thousands separators. Returns
// the original value unchanged if it's not a pure-digit numeric string, or if
// the magnitude is too small (< 1000) so we don't disfigure IDs like 367.
const tryFormatNumber = (value) => {
  if (value === null || value === undefined || value === "") return value;
  const str = String(value).trim();
  if (!/^-?\d+(\.\d+)?$/.test(str)) return value;
  const num = Number(str);
  if (!Number.isFinite(num) || Math.abs(num) < 1000) return value;
  const hasDecimals = str.includes(".");
  return new Intl.NumberFormat(NUMBER_LOCALE, {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: hasDecimals ? 2 : 0,
  }).format(num);
};

export default function ChangesPanel({
  info = [],
  changedCount = 0,
  riskIndicator = null,
  riskResults = null,
  riskLoading = false,
  role = "",
  fetchs,
  alert,
  alertEventId,
  currentUser,
  transactionalProjection = null,
  onResolved,
}) {
  const [expandedRow, setExpandedRow] = useState(null);
  const hiddenFields = ["LegalAddressId", "LegalAddressStateName", "LegalAddressCountryName"];

  const hiddenSet = new Set((hiddenFields || []).map((f) => String(f).toLowerCase().trim()));

  const filteredInfo = (info || []).filter((item) => {
    if (!item) return false;
    if (!item.key) return true;
    return !hiddenSet.has(String(item.key).toLowerCase());
  });

  const insertedAttributes = filteredInfo.filter((item) => item.action === "INS");
  const modifiedAttributes = filteredInfo.filter((item) => item.action === "UPD");
  const deletedAttributes = filteredInfo.filter((item) => item.action === "DLT");

  const groupedInsertedAttributes = insertedAttributes.reduce((acc, item) => {
    const group = item.group || "Ungrouped";
    if (!acc[group]) acc[group] = [];
    acc[group].push(item);
    return acc;
  }, {});

  const groupedModifiedAttributes = modifiedAttributes.reduce((acc, item) => {
    const group = item.group || "Ungrouped";
    if (!acc[group]) acc[group] = [];
    acc[group].push(item);
    return acc;
  }, {});

  const groupedDeletedAttributes = deletedAttributes.reduce((acc, item) => {
    const group = item.group || "Ungrouped";
    if (!acc[group]) acc[group] = [];
    acc[group].push(item);
    return acc;
  }, {});

  const toggleRow = (section, group) => {
    const key = `${section}-${group}`;
    setExpandedRow((prevKey) => (prevKey === key ? null : key));
  };

  const GetRiskClassName = (riskTextScore) => {
    const riskLabel = String(riskTextScore || "").toLowerCase();
    switch (riskLabel) {
      case "low":
      case "conservative":
        return "green";
      case "medium":
      case "moderate":
        return "orange";
      case "high":
      case "agressive":
        return "red";
      default:
        return undefined;
    }
  };

  const fieldTranslations = {
    LegalAddressCountryName: "Country",
    LegalAddressCountryId: "Country",
    LegalAddressCountryIso: "Country ISO",
    LegalAddressStateName: "State",
    LegalAddressCityName: "City",
    LegalAddressCityId: "City",
    LegalAddressStreet: "Address",
    LegalAddressNumber: "Number",
    LegalAddressZipCode: "Zip Code",
    LegalAddressType: "Type",
    LegalAddressExtraInformation1: "Neighbourhood",
    LegalAddressExtraInformation2: "References",
    LegalAddressReferences: "References",

    PersonActivitySectorId: "Activity Sector",
    PersonProfessionId: "Profession",
    PersonCurrencyType: "Currency Type",
    PersonStatus: "Status",
    PersonRisk: "Risk Level",
    PersonRelatedFrom: "Related From",
    PersoUserGUID: "User GUID",
    PersonObligatedEntity: "Obligated Entity",
    PersonSubAssetClassId: "Sub Asset Class",

    PhysicalPersonSecondName: "Second Name",
    PhysicalPersonCountryOfBirthName: "Country of Birth",
    PhysicalPersonCountryOfBirthId: "Country of Birth",
    PhysicalPersonPublicPositionId: "Public Position",

    SpouseFirstName: "Spouse First Name",
    SpouseSecondName: "Spouse Second Name",
    SpouseFirstLastName: "Spouse Last Name",
    SpouseSecondLastName: "Spouse Second Last Name",
    SpouseDocumentNumber: "Spouse Document Number",

    PersonLinkPublicPositionName: "Public Position",
    PersonLinkPublicPositionId: "Public Position",
    JuridicalPersonPublicPosition: "Public Position",

    JuridicalPersonConstPlaceId: "Place of Incorporation",
    JuridicalPersonSocietyTypeId: "Society Type",
    JuridicalPersonEntityTypeId: "Entity Type",

    PersonAnualIncome: "Annual Income",
    PersonExtraIncomeVolume: "Annual Extra Income",
    KYCAdvisorComments: "Advisor summary",
    KYCValidateComments: "Compliance summary",
    KYCDate: "KYC Advisor Date",
    KYCValidatedDate: "KYC Compliance Date",
  };

  const generateSummary = (items) => {
    const previousValues = items.map((item) => tryFormatNumber(item.oldValueName || item.oldValue || "-")).join(", ");
    const updatedValues = items.map((item) => tryFormatNumber(item.newValueName || item.newValue || "-")).join(", ");
    return { previous: previousValues, updated: updatedValues };
  };

  const renderRiskBadge = (label) => (
    <Badge
      style={{
        backgroundColor: GetRiskClassName(label) || "gray",
        color: "white",
        minWidth: "90px",
        padding: "5px 8px",
        textAlign: "center",
        textTransform: "capitalize",
        fontSize: "0.8rem",
      }}
    >
      {label || "N/A"}
    </Badge>
  );

  return (
    <div style={wrapperStyle}>
      <div style={headerCardStyle}>
        <span style={{ fontWeight: 600, color: "#263238" }}>Changes ({changedCount})</span>
        {riskIndicator && <span className={style.riskIndicator}>{riskIndicator}</span>}

        {(riskResults ||
          riskLoading ||
          (transactionalProjection &&
            (transactionalProjection.currentLimit !== undefined ||
              transactionalProjection.projectedLimit !== undefined))) && (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "24px",
            }}
          >
            <span
              style={{
                fontSize: "11px",
                fontWeight: 600,
                letterSpacing: "0.5px",
                color: "#607d8b",
                textTransform: "uppercase",
              }}
            >
              Evolution
            </span>

            {!riskResults && riskLoading && (
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "0.85rem",
                  color: "#90a4ae",
                  fontStyle: "italic",
                }}
              >
                <CircularProgress sm style={{ margin: "0px" }} />
                Calculating risk &amp; score…
              </span>
            )}

            {riskResults && (
              <React.Fragment>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    fontSize: "0.9rem",
                    fontWeight: "bold",
                  }}
                >
                  <span>Risk:</span>
                  {renderRiskBadge(riskResults.OriginTextScore)}
                  <span style={{ fontSize: "1rem" }}>→</span>
                  {renderRiskBadge(riskResults.FutureTextScore)}
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    fontSize: "0.9rem",
                    fontWeight: "bold",
                  }}
                >
                  <span>Score:</span>
                  <strong style={{ fontSize: "0.9rem" }}>
                    {riskResults.OriginNumericScore || 0} → {riskResults.FutureNumericScore || 0}
                  </strong>
                </div>
              </React.Fragment>
            )}

            {transactionalProjection &&
              (transactionalProjection.currentLimit !== undefined ||
                transactionalProjection.projectedLimit !== undefined) && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    fontSize: "0.9rem",
                    fontWeight: "bold",
                  }}
                >
                  <span>Transactional limit:</span>
                  <strong style={{ fontSize: "0.9rem" }}>
                    {formatLimit(transactionalProjection.currentLimit)} →{" "}
                    {formatLimit(transactionalProjection.projectedLimit)}
                  </strong>
                  {Array.isArray(transactionalProjection.errors) &&
                    transactionalProjection.errors.length > 0 && (
                      <span
                        title={transactionalProjection.errors.join("\n")}
                        style={{ color: "#e74c3c", fontSize: "0.85rem", fontWeight: 500 }}
                  >
                    <i
                      className="fa fa-exclamation-triangle"
                      style={{ marginRight: 4 }}
                      aria-hidden="true"
                    />
                    {transactionalProjection.errors.length} issue
                    {transactionalProjection.errors.length > 1 ? "s" : ""}
                  </span>
                )}
                </div>
              )}
          </div>
        )}
      </div>

      {insertedAttributes.length > 0 && (
        <div style={sectionCardStyle}>
          <div style={sectionTitleStyle}>Inserted Risk Attributes</div>
          <Table className={style.table} style={tableStyle}>
            <thead>
              <tr>
                <th>Field</th>
                <th>New value</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(groupedInsertedAttributes).map(([group, items]) => {
                const isComposite = group !== "Ungrouped";
                const summary = isComposite ? generateSummary(items) : null;

                return (
                  <React.Fragment key={group}>
                    {isComposite ? (
                      <React.Fragment>
                        <tr onClick={() => toggleRow("inserted", group)} className={style.clickableRow}>
                          <td>{group}</td>
                          <td style={newCellStyle}>{summary.updated}</td>
                        </tr>
                        {expandedRow === `inserted-${group}` && (
                          <tr>
                            <td colSpan={2}>
                              <Collapse isOpen={expandedRow === `inserted-${group}`}>
                                <div className={style.expandedRow}>
                                  <Table className={style.detailsTable} style={tableStyle}>
                                    <thead>
                                      <tr>
                                        <th>Field</th>
                                        <th>New Value</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {items.map((item) => (
                                        <tr key={item.key}>
                                          <td>{fieldTranslations[item.key] || item.key}</td>
                                          <td style={newCellStyle}>
                                            {tryFormatNumber(item.newValueName || item.newValue || "-")}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </Table>
                                </div>
                              </Collapse>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ) : (
                      items.map((item) => (
                        <tr key={item.key}>
                          <td>{fieldTranslations[item.key] || item.key}</td>
                          <td style={newCellStyle}>{tryFormatNumber(item.newValueName || item.newValue || "-")}</td>
                        </tr>
                      ))
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}

      {modifiedAttributes.length > 0 && (
        <div style={sectionCardStyle}>
          <div style={sectionTitleStyle}>Modified Risk Attributes</div>
          <Table className={style.table} style={tableStyle}>
            <thead>
              <tr>
                <th>Field</th>
                <th>Current value</th>
                <th>Updated value</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(groupedModifiedAttributes).map(([group, items]) => {
                const isComposite = group !== "Ungrouped";
                const summary = isComposite ? generateSummary(items) : null;

                return (
                  <React.Fragment key={group}>
                    {isComposite ? (
                      <React.Fragment>
                        <tr onClick={() => toggleRow("modified", group)} className={style.clickableRow}>
                          <td>{group}</td>
                          <td style={prevCellStyle}>{summary.previous}</td>
                          <td style={newCellStyle}>{summary.updated}</td>
                        </tr>
                        {expandedRow === `modified-${group}` && (
                          <tr>
                            <td colSpan={3}>
                              <Collapse isOpen={expandedRow === `modified-${group}`}>
                                <div className={style.expandedRow}>
                                  <Table className={style.detailsTable} style={tableStyle}>
                                    <thead>
                                      <tr>
                                        <th>Field</th>
                                        <th>Current value</th>
                                        <th>Updated value</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {items.map((item) => {
                                        const unchanged = item.oldValueName === item.newValueName;
                                        return (
                                          <tr key={item.key}>
                                            <td>{fieldTranslations[item.key] || item.key}</td>
                                            <td style={unchanged ? unchangedCellStyle : prevCellStyle}>
                                              {tryFormatNumber(item.oldValueName || item.oldValue || "-")}
                                            </td>
                                            <td style={unchanged ? unchangedCellStyle : newCellStyle}>
                                              {tryFormatNumber(item.newValueName || item.newValue || "-")}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </Table>
                                </div>
                              </Collapse>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ) : (
                      items.map((item) => {
                        const unchanged = item.oldValueName === item.newValueName;
                        return (
                          <tr key={item.key}>
                            <td>{fieldTranslations[item.key] || item.key}</td>
                            <td style={unchanged ? unchangedCellStyle : prevCellStyle}>
                              {tryFormatNumber(item.oldValueName || item.oldValue || "-")}
                            </td>
                            <td style={unchanged ? unchangedCellStyle : newCellStyle}>
                              {tryFormatNumber(item.newValueName || item.newValue || "-")}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}

      {deletedAttributes.length > 0 && (
        <div style={sectionCardStyle}>
          <div style={sectionTitleStyle}>Deleted Risk Attributes</div>
          <Table className={style.table} style={tableStyle}>
            <thead>
              <tr>
                <th>Field</th>
                <th>Current value</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(groupedDeletedAttributes).map(([group, items]) => {
                const isComposite = group !== "Ungrouped";
                const summary = isComposite ? generateSummary(items) : null;

                return (
                  <React.Fragment key={group}>
                    {isComposite ? (
                      <React.Fragment>
                        <tr onClick={() => toggleRow("deleted", group)} className={style.clickableRow}>
                          <td>{group}</td>
                          <td style={prevCellStyle}>{summary.previous}</td>
                        </tr>
                        {expandedRow === `deleted-${group}` && (
                          <tr>
                            <td colSpan={2}>
                              <Collapse isOpen={expandedRow === `deleted-${group}`}>
                                <div className={style.expandedRow}>
                                  <Table className={style.detailsTable} style={tableStyle}>
                                    <thead>
                                      <tr>
                                        <th>Field</th>
                                        <th>Current value</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {items.map((item) => (
                                        <tr key={item.key}>
                                          <td>{fieldTranslations[item.key] || item.key}</td>
                                          <td style={prevCellStyle}>
                                            {tryFormatNumber(item.oldValueName || item.oldValue || "-")}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </Table>
                                </div>
                              </Collapse>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ) : (
                      items.map((item) => (
                        <tr key={item.key}>
                          <td>{fieldTranslations[item.key] || item.key}</td>
                          <td style={prevCellStyle}>{tryFormatNumber(item.oldValueName || item.oldValue || "-")}</td>
                        </tr>
                      ))
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}

      {insertedAttributes.length === 0 &&
        modifiedAttributes.length === 0 &&
        deletedAttributes.length === 0 && (
          <div style={{ ...sectionCardStyle, textAlign: "center", color: "#90a4ae" }}>
            No changes to display.
          </div>
        )}

      {(alert || alertEventId) && (
        <ChangesActions
          alert={alert}
          alertEventId={alertEventId}
          role={role}
          fetchs={fetchs}
          currentUser={currentUser}
          onResolved={onResolved}
        />
      )}
    </div>
  );
}
